import axios from "axios";
import { apiGetSmartViewsCount, apiGetTaskCount } from "../network/api";
import { CONSTANTS, widgetTypes } from "../utils/constants";
import { getMarvinToken } from "../utils/authUtils";
import { SMARTVIEW_TYPE_MAPPING } from "../utils/widgetHelper";
import { mavisHost } from "../utils/config";
export const getCache = () => {
  return JSON.parse(localStorage.getItem(CONSTANTS.countCacheName) || "{}");
};

export const getInitialCache = () => {
  const authDetails = JSON.parse(localStorage.getItem("auth") || "{}");
  const userId = authDetails?.User?.Id;
  const counterCache = getCache();
  if (counterCache.userId && counterCache.userId === userId)
    return counterCache;
  return { userId };
};

export const initializeDashboardsCache = (dashboards = []) => {
  const counterCache = getInitialCache();
  const temp = { userId: counterCache.userId };
  const currentTime = new Date().toISOString();
  dashboards.forEach((dashboard) => {
    const dashboardCache = counterCache?.[dashboard.id] || {};
    const updatedWidgets = {};
    dashboard?.layout?.widgets?.forEach((widget) => {
      if (
        widget.widgetType === widgetTypes.SingleCounter ||
        widget.widgetType === widgetTypes.ProgressBar ||
        widget.widgetType === widgetTypes.SpeedoMeter ||
        widget.widgetType === widgetTypes.Calendar ||
        widget.widgetType === widgetTypes.Banner
      )
        updatedWidgets[widget.id] = dashboardCache[widget.id] || {};
    });
    temp[dashboard.id] = {
      ...updatedWidgets,
      [CONSTANTS.lastForceRefreshedTime]:
        dashboardCache[CONSTANTS.lastForceRefreshedTime] || currentTime,
    };
  });
  localStorage.setItem(CONSTANTS.countCacheName, JSON.stringify(temp));
};

export const CACHE_TIME_MINUTES = 10;
export const enableCache = true;
const countCacheName = CONSTANTS.countCacheName;
export const cacheExpirationTime = CACHE_TIME_MINUTES * 60 * 1000;

export const getCachedData = (
  forceRefresh,
  widgetCache,
  updatedAt,
  cacheExpirationTime
) => {
  const currentTime = Date.now();
  if (
    enableCache &&
    !forceRefresh &&
    widgetCache?.timestamp &&
    widgetCache.data &&
    currentTime - widgetCache.timestamp < cacheExpirationTime &&
    new Date(updatedAt) < new Date(widgetCache.timestamp)
  ) {
    return widgetCache.data;
  }
  return null;
};

export const getMavisAuthorizationHeader = async () => {
  let authorizationHeader = sessionStorage.getItem('casa_mavis_authorization_header');
  try {
    if (!authorizationHeader) {
      const { data } = await axios.post(
        `${mavisHost}api/external/Authentication`,
        {},
        {
          headers: {
            "MarvinToken": getMarvinToken()
          }
        }
      )
      authorizationHeader = data?.AuthKey;
    }
    sessionStorage.setItem('casa_mavis_authorization_header', authorizationHeader);
  } catch (error) {
    console.error("Error fetching mavis auth header:", error);
    return null; // Return null if there's an error
  }
  return authorizationHeader;
}

export const getAdvancedSearchText = (smartViewTab) => {
  return (smartViewTab?.tabContentConfigurationResponseDto?.fetchCriteria?.additionalData 
    && JSON.parse(smartViewTab.tabContentConfigurationResponseDto.fetchCriteria.additionalData).Marvin?.AdvancedSearchText)
     || JSON.parse(smartViewTab.tabContentConfigurationResponseDto.fetchCriteria.advancedSearchText);
}

export const getSmartViewToken = (smartViewTab) => {
  const AdvancedSearchText = getAdvancedSearchText(smartViewTab);
  return AdvancedSearchText?.smartviewToken;
}

export const getMavisSmartViewRequestData = (smartViewTab) => {
  const advancedSearchText = getAdvancedSearchText(smartViewTab);
  const databaseId = advancedSearchText?.DatabaseId;
  const tableId = advancedSearchText?.TableId;
  const filter = advancedSearchText?.filter;
  const marvinToken = getSmartViewToken(smartViewTab);
  const requestBody = {
    "Paging": {
        "PageIndex": 1,
        "PageSize": 1
    },
    "Select": [ "mv_RowId" ],
    "AdminFilter": {
        [filter?.AndCriteria || '$and']: filter?.filterRows?.map((row) => {
          return {
            [row?.column] : {
              [row?.operator] : [row?.value]
            }
          }
        })
    }
  };
  return { databaseId, tableId, requestBody, marvinToken };
}

export const getMavisSmartViewCount = async (requestData) => {
  try {
    const mavisAuthHeader = await getMavisAuthorizationHeader();
    const { databaseId, tableId, requestBody, marvinToken } = requestData;
    const response = await axios.post(
      `${mavisHost}api/external/${databaseId}/${tableId}/rows/query`,
      requestBody,
      {
        headers: {
          "authorization": mavisAuthHeader,
          "externalauthkey": marvinToken,
        }
      }
    );
    return { count: response.data?.count };
  } catch (error) {
    console.error("Error fetching smart view mavis count:", error);
    return null; // Return null if there's an error
  }
}

export const fetchData = async (body, mappedText) => {
  if (mappedText === SMARTVIEW_TYPE_MAPPING[99]) return await getMavisSmartViewCount(body);
  if (mappedText) return apiGetSmartViewsCount(body, mappedText);
  return apiGetTaskCount(body);
};

export const updateCache = (activeDashboardId, id, newCacheData) => {
  const currentTime = Date.now();
  const counterCache = JSON.parse(localStorage.getItem(countCacheName) || "{}");
  counterCache[activeDashboardId] = {
    ...counterCache[activeDashboardId],
    [id]: {
      data: newCacheData,
      timestamp: currentTime,
    },
  };
  localStorage.setItem(countCacheName, JSON.stringify(counterCache));
};

export const getCountsData = async (
  forceRefresh,
  widget,
  activeDashboardId,
  postBody,
  getTotalCount,
  mappedText
) => {
  const { id, updatedAt } = widget;
  const counterCache = JSON.parse(localStorage.getItem(countCacheName) || "{}");
  const widgetCache = counterCache?.[activeDashboardId]?.[id];
  const cachedData = getCachedData(
    forceRefresh,
    widgetCache,
    updatedAt,
    cacheExpirationTime
  );
  if (cachedData) return cachedData;
  try {
    const apis = [fetchData(postBody, mappedText)];
    if (getTotalCount) {
      const totalPostBody = {
        ...postBody,
        filters: {
          ...postBody.filters,
          statusCode: "-1",
          includeOverdue: false,
        },
      };
      apis.push(fetchData(totalPostBody));
    }
    const [fetchedData = {}, maxCountData = {}] = await Promise.all(apis);
    const newCacheData = getTotalCount
      ? { count: fetchedData?.count, maxCount: maxCountData?.count }
      : { count: fetchedData?.count };
    updateCache(activeDashboardId, id, newCacheData);
    return newCacheData;
  } catch (error) {
    console.error("Error fetching counts data:", error);
    throw error;
  }
};
